/**
 * QUÉ FOCO SE PROPONE al confirmar la plaga de un muestreo.
 *
 * Una sola pieza para las TRES pantallas donde se confirma (panel de triaje,
 * resumen del asistente de captura y vista previa de voz): recibe EL SITIO y la
 * plaga elegida, baja los focos de ese sector con sus muestreos, y le pasa todo a
 * la regla pura (`focusLinking.ts`). Aquí no se decide nada.
 *
 * Se pide POR SECTOR, el de un parte que ya se abrió desde una lista recortada
 * (o el que un encargado está eligiendo entre SUS sectores): por eso está exento
 * del barrido de alcance, igual que la ficha del parte. La base de datos vuelve a
 * comprobar el alcance al confirmar (`greenhouse_in_member_scope` en la RPC).
 *
 * `seenFocusIds` viaja con la confirmación: es la garantía de que nadie crea un
 * foco nuevo saltándose uno que apareció mientras tenía la pantalla abierta.
 */
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { buildPestCanon, type RawPest } from "@/lib/health/pestCanon";
import {
  PRECISE_SOURCE,
  proposeLink,
  summarizeFocus,
  type FocusMember,
  type FocusSummary,
  type LinkHint,
  type LinkResult,
} from "@/lib/health/focusLinking";

/** Dónde y cuándo se hizo el muestreo que se está confirmando. */
export interface SamplingPlace {
  sessionId: string | null;
  blockId: string | null;
  rowId: string | null;
  lineNumber: number | null;
  /** Posición 0..1 a lo largo de la línea, y de dónde salió. */
  anchorT: number | null;
  anchorSource: string | null;
  accuracyM: number | null;
  /** Si falta, se toma de la línea (`rowId`). */
  rowLengthM?: number | null;
  date: string;
  hint?: LinkHint | null;
}

export interface BlockContext {
  foci: { id: string; block_id: string; block_row_id: string | null; line_number: number | null; pest_id: string | null; pest_name_snapshot: string; crop_cycle_id: string | null; focus_number: number }[];
  members: (FocusMember & { focusId: string })[];
  rows: { id: string; line_number: number; length_m: number | null; is_active: boolean }[];
  cycleStarts: string[];
  pests: RawPest[];
}

/**
 * Los focos de UN sector con sus muestreos, sus líneas, los inicios de ciclo y el
 * catálogo de plagas. Lo comparten la propuesta de enlace (al confirmar) y la
 * pregunta al trabajador (al capturar): la misma foto del sector para las dos.
 */
export async function fetchBlockFociContext(companyId: string, blockId: string): Promise<BlockContext> {
  const [fociRes, rowsRes, cyclesRes, pestsRes] = await Promise.all([
    supabase.from("pest_foci").select("id, block_id, block_row_id, line_number, pest_id, pest_name_snapshot, crop_cycle_id, focus_number").eq("block_id", blockId).eq("company_id", companyId),
    supabase.from("block_rows").select("id, line_number, length_m, is_active").eq("block_id", blockId),
    supabase.from("crop_cycles").select("start_date").eq("block_id", blockId),
    supabase.from("pests").select("id, name, scientific_name"),
  ]);
  if (fociRes.error) throw fociRes.error;
  const foci = (fociRes.data ?? []) as unknown as BlockContext["foci"];
  const rows = (rowsRes.data ?? []) as unknown as BlockContext["rows"];
  const rowById = new Map(rows.map((r) => [r.id, r]));

  let members: BlockContext["members"] = [];
  if (foci.length > 0) {
    const { data, error } = await supabase
      .from("pest_scouting_sessions")
      .select("id, focus_id, date, created_at, block_row_id, anchor_t, anchor_source, gps_accuracy_m, severity, review_status, resolved_at, triage_status, dismissal_reason, photo_url, photo_urls")
      .in("focus_id", foci.map((f) => f.id))
      .eq("company_id", companyId);
    if (error) throw error;
    type S = { id: string; focus_id: string; date: string; created_at: string | null; block_row_id: string | null; anchor_t: number | null; anchor_source: string | null; gps_accuracy_m: number | null; severity: string | null; review_status: string; resolved_at: string | null; triage_status: string; dismissal_reason: string | null; photo_url: string | null; photo_urls: string[] | null };
    members = ((data ?? []) as unknown as S[])
      .filter((s) => s.triage_status === "classified" || (s.triage_status === "dismissed" && s.dismissal_reason === "clean_check"))
      .map((s) => {
        const row = s.block_row_id ? rowById.get(s.block_row_id) : undefined;
        return {
          focusId: s.focus_id,
          id: s.id,
          kind: s.triage_status === "dismissed" ? ("clean_check" as const) : ("sampling" as const),
          date: s.date,
          createdAt: s.created_at,
          rowId: s.block_row_id,
          lineNumber: row?.line_number ?? null,
          rowActive: row?.is_active ?? false,
          anchorT: s.anchor_t,
          anchorSource: s.anchor_source,
          accuracyM: s.gps_accuracy_m,
          rowLengthM: row?.length_m ?? null,
          severity: s.severity,
          resolved: s.review_status === "resolved",
          resolvedAt: s.resolved_at,
          photoUrl: s.photo_urls?.[0] ?? s.photo_url ?? null,
        };
      });
  }
  return {
    foci,
    members,
    rows,
    cycleStarts: ((cyclesRes.data ?? []) as { start_date: string | null }[]).map((c) => c.start_date).filter(Boolean) as string[],
    pests: (pestsRes.data ?? []) as unknown as RawPest[],
  };
}

/** Los focos del sector, resumidos para la regla. `excludeSessionId` = el parte que se está decidiendo. */
export function summariesOf(ctx: BlockContext, excludeSessionId: string | null): FocusSummary[] {
  const canon = buildPestCanon(ctx.pests);
  const rowById = new Map(ctx.rows.map((r) => [r.id, r]));
  const out: FocusSummary[] = [];
  for (const f of ctx.foci) {
    const row = f.block_row_id ? rowById.get(f.block_row_id) : undefined;
    const s = summarizeFocus(
      {
        id: f.id,
        blockId: f.block_id,
        rowId: f.block_row_id,
        lineNumber: f.line_number ?? row?.line_number ?? null,
        rowActive: row ? row.is_active : f.block_row_id == null,
        pestKey: canon.key(f.pest_id, f.pest_name_snapshot),
        pestName: f.pest_name_snapshot,
        cropCycleId: f.crop_cycle_id,
      },
      // El propio parte no cuenta como miembro de nadie mientras se decide a dónde va.
      ctx.members.filter((m) => m.focusId === f.id && m.id !== excludeSessionId),
    );
    if (s) out.push(s);
  }
  return out;
}

/** El contexto que la regla necesita de un sector: sus líneas activas en orden y sus inicios de ciclo. */
export const linkContextOf = (ctx: BlockContext, blockId: string) => ({
  lineOrdinals: new Map([[blockId, ctx.rows.filter((r) => r.is_active).map((r) => r.line_number).sort((a, b) => a - b)]]),
  cycleStarts: new Map([[blockId, [...ctx.cycleStarts].sort()]]),
});

export function useFocusLinkProposal(place: SamplingPlace | null, pestId: string | null, enabled: boolean) {
  const { company } = useAuth();
  const blockId = place?.blockId ?? null;

  const query = useQuery({
    queryKey: ["focus-link-candidates", company?.id, blockId],
    enabled: enabled && !!company?.id && !!blockId,
    staleTime: 0, // se pide fresco al abrir: lo que se enseña es lo que se va a poder confirmar
    retry: 1, // sin red, que se sepa pronto: el asistente de captura no espera por esto
    queryFn: () => fetchBlockFociContext(company!.id, blockId!),
  });

  const computed = useMemo(() => {
    const ctx = query.data;
    if (!ctx || !place?.blockId) return null;
    const canon = buildPestCanon(ctx.pests);
    const summaries = summariesOf(ctx, place.sessionId);
    const pestName = pestId ? ctx.pests.find((p) => p.id === pestId)?.name ?? null : null;
    const ownRow = place.rowId ? ctx.rows.find((r) => r.id === place.rowId) : undefined;
    const rowLengthM = place.rowLengthM ?? ownRow?.length_m ?? null;
    const alongM = place.anchorSource === PRECISE_SOURCE && place.anchorT != null && rowLengthM != null ? place.anchorT * rowLengthM : null;
    const result: LinkResult = proposeLink(
      {
        id: place.sessionId,
        blockId: place.blockId,
        rowId: place.rowId,
        // La línea de verdad es la de su fila; el número suelto (notas «Línea: N») solo si no hay fila.
        lineNumber: ownRow?.line_number ?? place.lineNumber ?? null,
        alongM,
        anchorSource: place.anchorSource,
        accuracyM: place.accuracyM,
        pestKey: pestId ? canon.key(pestId, pestName) : null,
        date: place.date,
        cropCycleId: null,
        hint: place.hint ?? null,
      },
      summaries,
      linkContextOf(ctx, place.blockId),
    );
    return {
      result,
      summaries,
      focusNumber: new Map(ctx.foci.map((f) => [f.id, f.focus_number])),
      focusPestId: new Map(ctx.foci.map((f) => [f.id, f.pest_id])),
      seenFocusIds: ctx.foci.map((f) => f.id),
    };
  }, [query.data, place, pestId]);

  return { ...computed, loading: query.isLoading, error: query.error, refetch: query.refetch };
}
